import { CommonState, GenericComments } from "models/stateTypes/commonState";
import { CommonActionType } from "models/actionTypes/commonActionTypes";
import { ReduxConstants } from "utils/constants";
import { RootActionType } from "models/actionTypes/rootActionTypes";
import { LocaleEnums } from "models/enums";
import { LocalizationHelperInstance } from "utils/helpers";

const initialState: CommonState = {
  dimensions: {
    incomingInvoiceDimensions: [],
    receiptDimensions: [],
    invoiceDimensions: [],
    cashReconciliationDimensions: [],
    allDimensions: [],
    allDimensionTypes: undefined,
    incomingInvoiceDimensionSuggestions: [],
    receiptDimensionSuggestions: [],
    invoiceDimensionSuggestions: [],
    cashReconciliationDimensionSuggestions: [],
  },
  locale: {
    language: LocaleEnums.LocaleType.sv,
    intl: LocalizationHelperInstance.getIntl(LocaleEnums.LocaleType.sv),
  },
  genericComments: {} as GenericComments,
  cancelledRequests: [],
  supportCategories: [],
  supportCategoryOptions: { agencyMode: false },
  companyAccountingDoneUntilMonth: undefined,
  companyLastReconciliation: undefined,
};

export const commonReducer = (
  state: CommonState = initialState,
  action: CommonActionType | RootActionType
): CommonState => {
  switch (action.type) {
    case ReduxConstants.RootActions.RESET:
      return initialState;
    case ReduxConstants.CommonActions.INCOMING_INVOICE_DIMENSIONS:
      return {
        ...state,
        dimensions: {
          ...state.dimensions,
          incomingInvoiceDimensions: action.payload,
        },
      };
    case ReduxConstants.CommonActions.RECEIPT_DIMENSIONS:
      return {
        ...state,
        dimensions: {
          ...state.dimensions,
          receiptDimensions: action.payload,
        },
      };
    case ReduxConstants.CommonActions.INVOICE_DIMENSIONS:
      return {
        ...state,
        dimensions: {
          ...state.dimensions,
          invoiceDimensions: action.payload,
        },
      };
    case ReduxConstants.CommonActions.CASH_RECONCILIATION_DIMENSIONS:
      return {
        ...state,
        dimensions: {
          ...state.dimensions,
          cashReconciliationDimensions: action.payload,
        },
      };
    case ReduxConstants.CommonActions.ALL_DIMENSIONS:
      return {
        ...state,
        dimensions: {
          ...state.dimensions,
          allDimensions: action.payload,
          allDimensionTypes: action.payload.map((d) => d.key) ?? [],
        },
      };
    case ReduxConstants.CommonActions.INCOMING_INVOICE_DIMENSION_SUGGESTIONS:
      return {
        ...state,
        dimensions: {
          ...state.dimensions,
          incomingInvoiceDimensionSuggestions: action.payload,
        },
      };
    case ReduxConstants.CommonActions.RECEIPT_DIMENSION_SUGGESTIONS:
      return {
        ...state,
        dimensions: {
          ...state.dimensions,
          receiptDimensionSuggestions: action.payload,
        },
      };
    case ReduxConstants.CommonActions.INVOICE_DIMENSION_SUGGESTIONS:
      return {
        ...state,
        dimensions: {
          ...state.dimensions,
          invoiceDimensionSuggestions: action.payload,
        },
      };
    case ReduxConstants.CommonActions.CASH_RECONCILIATION_DIMENSIONS_SUGGESTIONS:
      return {
        ...state,
        dimensions: {
          ...state.dimensions,
          cashReconciliationDimensionSuggestions: action.payload,
        },
      };
    case ReduxConstants.CommonActions.TOGGLE_LANGUAGE:
      const lang =
        state.locale.language === LocaleEnums.LocaleType.sv ? LocaleEnums.LocaleType.en : LocaleEnums.LocaleType.sv;
      return {
        ...state,
        locale: {
          language: lang,
          intl: LocalizationHelperInstance.getIntl(lang),
        },
      };
    case ReduxConstants.CommonActions.LOAD_GENERIC_COMMENTS:
      return {
        ...state,
        genericComments: action.payload,
      };
    case ReduxConstants.CommonActions.CANCEL_REQUEST:
      return {
        ...state,
        cancelledRequests: [...state.cancelledRequests, action.payload],
      };
    case ReduxConstants.CommonActions.PROFILESETTINGS_LANGUAGE:
      return {
        ...state,
        locale: {
          language: action.payload,
          intl: LocalizationHelperInstance.getIntl(action.payload),
        },
      };
    case ReduxConstants.CommonActions.LOAD_SUPPORT_ISSUE_CATEGORIES:
      return {
        ...state,
        supportCategories: action.payload.categories ?? [],
        supportCategoryOptions: {
          agencyMode: action.payload.agencyMode,
          agencyName: action.payload.agencyName,
          recipientOptions: action.payload.recipientOptions,
          companyId: action.payload.companyId,
        },
      };
    case ReduxConstants.CommonActions.SET_COMPANY_ACCOUNTING_DONE_UNTIL:
      return {
        ...state,
        companyAccountingDoneUntilMonth: action.payload,
      };
    case ReduxConstants.CommonActions.SET_COMPANY_LAST_RECONCILIATION:
      return {
        ...state,
        companyLastReconciliation: action.payload,
      };
    default:
      return state;
  }
};
